import {
  useWSSnackbar,
  WSActions,
  WSAvatar,
  WSDropFileInput,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSIcon,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useState } from "react";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useMemberPrivateFileCreate } from "../../../../query/files/mutations";
import { useMutationUpdateWorkLog } from "../../../../query/workLog/mutations/useMutationUpdateWorkLog";
import { WS_LINKS } from "../../../../types/wsLinks";

type Props = {
  workLogId: string;
  payerId: string;
  payeeId: string;
  existingAttachmentIds: string[];
  onClose: (success: boolean) => void;
} & WSElementProps;

export const ModalUploadWorkLogAttachment: React.FC<Props> = ({
  workLogId,
  payerId,
  payeeId,
  existingAttachmentIds,
  onClose,
  ...elementProps
}) => {
  const [file, setFile] = useState<File | null>(null);
  const { openSnackbar } = useWSSnackbar();

  const [uploadFile, uploadFileMeta] = useMemberPrivateFileCreate({
    hidden: true,
    viewerIds: [payeeId, payerId].filter(Boolean)
  });

  const [updateWorkLog, updateWorkLogMeta] = useMutationUpdateWorkLog();

  const handleUpload = async () => {
    if (!file) return;

    const data = new FormData();
    data.append("file", file);

    try {
      const uploadResponse = await uploadFile(data, {
        throwOnError: true
      });

      const fileId = uploadResponse?.[0]?.fileId;

      if (fileId) {
        await updateWorkLog(
          {
            workLogId,
            request: {
              attachmentIds: [...existingAttachmentIds, fileId]
            }
          },
          {
            onSuccess: () => {
              openSnackbar({
                icon: { name: "check" },
                message: "Attachment uploaded"
              });
              onClose(true);
            },
            onError: () => {
              openSnackbar({
                message: "Failed to add attachment",
                type: "error"
              });
            },
            throwOnError: true
          }
        );
      }
    } catch {
      openSnackbar({
        message: "Failed to upload file",
        type: "error"
      });
    }
  };

  const isLoading = uploadFileMeta.isLoading || updateWorkLogMeta.isLoading;

  return (
    <WSElement {...elementProps}>
      <WSList gap="M">
        <WSInfoBox size="S" title="Reminder:">
          Your files are encrypted and secured. Keep in mind that file uploads
          are not designed for storing sensitive payment information (such as
          payment card numbers).{" "}
          <a href={WS_LINKS.security} target="_blank" rel="noopener noreferrer">
            Learn more
          </a>{" "}
          about data security.
        </WSInfoBox>

        <WSText.ParagraphSm color="gray500">
          Upload a file to attach to this work log. PDF, images, and documents
          are supported.
        </WSText.ParagraphSm>

        {file ? (
          <WSElement p="M" colorBackground="gray50">
            <WSFlexBox.CenterY wrap="nowrap">
              <WSAvatar.Icon
                icon="paperclip"
                mr="M"
                color="green500"
                colorBackground="green50"
              />
              <WSText.ParagraphSm singleLine style={{ flex: 1 }}>
                {file.name}
              </WSText.ParagraphSm>
              <WSIcon
                block
                color="gray500"
                size="XS"
                mx="M"
                name="exit"
                onClick={() => setFile(null)}
              />
            </WSFlexBox.CenterY>
          </WSElement>
        ) : (
          <WSElement>
            <WSDropFileInput
              name="attachment"
              buttonText="Browse files"
              buttonProps={{
                type: "button",
                icon: "upload"
              }}
              maxSize={100000000}
              onDropAccepted={accepted => {
                setFile(accepted[0]);
              }}
            />
          </WSElement>
        )}

        <WSErrorMessage
          contextKey="Other"
          error={uploadFileMeta.error || updateWorkLogMeta.error}
        />

        <WSActions
          alignment="fill"
          buttons={[
            {
              kind: "Tertiary",
              label: "Cancel",
              type: "button",
              onClick: () => onClose(false)
            },
            {
              kind: "Primary",
              label: "Add attachment",
              onClick: handleUpload,
              loading: isLoading,
              disabled: !file
            }
          ]}
        />
      </WSList>
    </WSElement>
  );
};
